import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/widgets";
import { ComplaintDrawer } from "@/components/ComplaintDrawer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import dayjs from "dayjs";
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend } from "recharts";
import { Factory } from "lucide-react";

const NAVY = "#0E2240";
const GOLD = "#BF9B30";

/* Stable recharts prop objects — recreating them inline forces chart re-renders. */
const TREND_MARGIN = { top: 8, right: 12, left: -20, bottom: 0 };
const AXIS_TICK = { fontSize: 11 };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const LEGEND_STYLE = { fontSize: 12 };

export default function OemDelaysPage() {
  const { user, fnFilter } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const effectiveFn = user?.function_scope !== "Both" ? user.function_scope : fnFilter;

  useEffect(() => {
    setLoading(true);
    api
      .get("/reports/oem-delays", { params: { function: effectiveFn } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [effectiveFn]);

  if (loading || !data)
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={`oem-skeleton-${i}`} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );

  const s = data.summary;
  const openComplaint = (id) => { setDrawerId(id); setDrawerOpen(true); };

  if (!s) {
    return (
      <div data-testid="oem-delays-page">
        <PageHeader
          title="OEM Delivery Delays"
          subtitle="Sales-only lane · factory-caused delays excluded from RAG, escalations and leaderboards"
        />
        <Card>
          <CardContent className="py-14">
            <EmptyState
              title="Sales-only tracking"
              subtitle="Delivery delay tracking applies to the Sales function only. Switch the function lens to Sales or Both to view OEM delays."
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="oem-delays-page">
      <PageHeader
        title="OEM Delivery Delays"
        subtitle="Sales-only lane · factory-caused delays (allocation, production, transit) · excluded from RAG, escalations and leaderboards · chased with the OEM"
      />

      {data.generated_on_fmt && (
        <p className="text-xs text-muted-foreground tabular -mt-3 mb-4" data-testid="oem-report-stamps">
          Generated on: {data.generated_on_fmt} · Based on data uploaded: {data.based_on_fmt || "—"}
        </p>
      )}

      <div className="rounded-[var(--radius)] bg-[hsl(var(--cx-navy))] text-white px-4 py-3 flex items-center gap-2 mb-4" data-testid="oem-page-banner">
        <Factory size={16} className="text-[hsl(var(--cx-gold))]" />
        <p className="text-sm">
          These delays are <strong>not the dealership's accountability</strong> — they are tracked here so the group can
          follow up with Kia and keep customers informed.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        {[
          ["Open OEM delays", s.open, "oem-kpi-open"],
          ["Avg days pending", s.avg_days_pending, "oem-kpi-avg"],
          ["Oldest (days)", s.oldest_days, "oem-kpi-oldest"],
          ["New this month", s.new_this_month, "oem-kpi-new"],
          ["Resolved this month", s.closed_this_month, "oem-kpi-closed"],
        ].map(([label, val, tid]) => (
          <Card key={label} className="p-4" data-testid={tid}>
            <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</p>
            <p className="kpi-value text-2xl sm:text-3xl mt-1">{val}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-7" data-testid="oem-trend-panel">
          <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal">6-Month OEM Delay Trend</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[220px]">
              {data.trend.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.trend} margin={TREND_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDF0F4" />
                    <XAxis dataKey="month" tick={AXIS_TICK} />
                    <YAxis tick={AXIS_TICK} allowDecimals={false} />
                    <RTooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                    <Line type="monotone" dataKey="opened" name="Raised" stroke={NAVY} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="closed" name="Resolved" stroke={GOLD} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState title="No OEM delay history" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5" data-testid="oem-by-reason-panel">
          <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal">By Delay Reason</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {data.by_reason.length === 0 ? (
              <EmptyState title="No OEM delays recorded" />
            ) : (
              <div className="space-y-2">
                {data.by_reason.map((r) => (
                  <div key={r.reason} className="flex items-center justify-between gap-2 border rounded-md px-3 py-2">
                    <span className="text-sm truncate">{r.reason}</span>
                    <span className="text-xs tabular whitespace-nowrap">
                      <strong className="text-sm">{r.open}</strong> open · {r.count} total
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-12" data-testid="oem-open-items-panel">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-base font-normal">Open OEM Delays — oldest first ({data.open_items.length})</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-0">
            {data.open_items.length === 0 ? (
              <EmptyState title="No open OEM delays" subtitle="All factory-caused delivery delays are resolved." />
            ) : (
              <div className="overflow-x-auto">
                <Table className="cx-table min-w-[820px]" data-testid="oem-open-items-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4">Complaint</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Outlet</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Days Pending</TableHead>
                      <TableHead>Reported</TableHead>
                      <TableHead className="pr-4">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.open_items.map((c) => (
                      <TableRow key={c.id} className="cursor-pointer" data-testid={`oem-item-row-${c.complaint_number}`}
                        onClick={() => openComplaint(c.id)}>
                        <TableCell className="pl-4 font-medium whitespace-nowrap">
                          {c.complaint_number}
                          <Badge variant="outline" className="ml-1.5 text-[0.6rem] bg-[hsl(var(--cx-navy))] text-white border-transparent">OEM</Badge>
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate">{c.customer_name}</TableCell>
                        <TableCell className="whitespace-nowrap">{c.outlet_code} <span className="text-xs text-muted-foreground">{c.outlet_name}</span></TableCell>
                        <TableCell className="max-w-[110px] truncate">{c.model || "—"}</TableCell>
                        <TableCell className="max-w-[160px] truncate text-xs">{c.delay_reason || "—"}</TableCell>
                        <TableCell className="text-right tabular font-semibold">{c.days_open}</TableCell>
                        <TableCell className="whitespace-nowrap tabular text-xs">{dayjs(c.date_reported).format("DD MMM YY")}</TableCell>
                        <TableCell className="pr-4"><StatusBadge status={c.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ComplaintDrawer complaintId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
